import { EVENTUNIT_SIZE, useEventUnit } from "components/common/EventUnit";
import Label, { useLabels } from 'components/general/Label';
import { Status } from 'components/presentation/Status';
import { useGetConfigSports, useGetDisciplines, useGetNocs, useGetParticipantNames, useGetScheduleStartList } from 'helpers/commonCodes';
import { completeRsc } from 'helpers/rsc';
import { ALL_URL, dateFormat, dateFormatNoTz, getDateTimeFormat } from 'helpers/utils'
import { useGetPath, useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import { Helmet } from 'react-helmet-async';
import { useLocation } from "react-router-dom";
import { useAbsoluteRoot } from "helpers/urlRoot";
import TeamHeaderBio from "components/common/TeamBioHeader";
import PageHeaderFlag from "components/common/PageHeaderFlag";
import SportCode, { getSportCodeObject } from "components/common/SportCode"
import { useQueryClient } from "@tanstack/react-query";

export function useGetHeaders() {
    let objSEO = useGetSEO();
    return (objSEO.h1Object);
}


export function SEOMetaTags() {
    const { urlRoot } = useAbsoluteRoot();
    let objSEO = useGetSEO();
    let location = useLocation();
    return (
        <Helmet>
            { /* Standard metadata tags */}
            <title>{objSEO.pageTitle}</title>
            <meta name='description' content={objSEO.pageDescription} />
            { /* End standard metadata tags */}
            { /* Facebook tags */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={location.pathname} />
            <meta property="og:title" content={objSEO.pageTitle} />
            <meta property="og:description" content={objSEO.pageDescription} />
            <meta property="og:image" content={`${urlRoot}assets/images/logo.png`} />
            { /* End Facebook tags */}
            { /* Twitter tags */}
            <meta property="twitter:card" content="summary" />
            <meta property="twitter:site" content="@Paris2024" />
            <meta property="twitter:url" content={location.pathname} />
            <meta property="twitter:title" content={objSEO.pageTitle} />
            <meta property="twitter:description" content={objSEO.pageDescription} />
            <meta property="twitter:image" content={`${urlRoot}assets/images/logo.png`} />
            { /* End Twitter tags */}
        </Helmet>
    )
}


function useGetSEO() {
    const { mirs_competition, mirs_language, mirs_discipline, mirs_rsc, mirs_date, mirs_code, mirs_event, mirs_noc } = useUrlFriendlyToParameters()

    const mirs_rsc_completed = completeRsc(mirs_rsc || mirs_event)
    let obj = useGetPath()
    let objUrl = obj(mirs_language);
    const fLabel = useLabels();

    const dataDisciplines = useGetDisciplines({
        mirs_competition: mirs_competition,
        mirs_language: mirs_language,
    });
    const queryClient = useQueryClient();
    const dataSportCodes = queryClient.getQueryData(["SportCodes", mirs_competition, mirs_language])?.sportCodes;


    const dataConfigSport = useGetConfigSports({ mirs_competition, mirs_discipline });
    const noH2hPhases = dataConfigSport?.noH2hPhases;
    const isH2H = Array.isArray(noH2hPhases) && noH2hPhases?.find(p => (new RegExp(p)).test(mirs_rsc_completed)) ? false : dataConfigSport?.isH2H
    const eu = useEventUnit();
    let eventDescSchedule = eu(mirs_rsc_completed, EVENTUNIT_SIZE.LONG)
    const scheduleSL = useGetScheduleStartList();
    const nocsList = useGetNocs({ mirs_competition, mirs_language });
    const dataParticipants = useGetParticipantNames({ mirs_competition, mirs_language });
    const scheduleItem = scheduleSL?.find(schedule => schedule?.code === mirs_rsc_completed)
    const scheduleStatus = scheduleItem?.status?.code
    const resultStatus = scheduleItem?.result?.status?.code
    const _lblStatus = Status({ typeToPrint: 'result', scheduleStatus: scheduleStatus, resultStatus: resultStatus, onlyTextH1: true })
    //COMMON DATA
    const discipline = dataDisciplines?.disciplines?.find((item) => item.code === mirs_discipline)?.description;
    const eventUnitLongDesc = eu(mirs_rsc_completed, EVENTUNIT_SIZE.LONG);
    const eventUnitDescription = eu(mirs_rsc_completed);
    const participant1TeamType = Array.isArray(scheduleItem?.start) && scheduleItem?.start?.find(item => item?.sortOrder === 1)?.participant?.teamType
    const participant2TeamType = Array.isArray(scheduleItem?.start) && scheduleItem?.start?.find(item => item?.sortOrder === 2)?.participant?.teamType
    const participant1Name = Array.isArray(scheduleItem?.start) && scheduleItem?.start?.find(item => item?.sortOrder === 1)?.participant?.name
    const participant2Name = Array.isArray(scheduleItem?.start) && scheduleItem?.start?.find(item => item?.sortOrder === 2)?.participant?.name
    const participant1Organisation = Array.isArray(scheduleItem?.start) && scheduleItem?.start?.find(item => item?.sortOrder === 1)?.participant?.organisation?.longDescription
    const participant2Organisation = Array.isArray(scheduleItem?.start) && scheduleItem?.start?.find(item => item?.sortOrder === 2)?.participant?.organisation?.longDescription

    let participant1CP = getSportCodeObject(dataSportCodes, mirs_discipline, '@CompetitorPlace', scheduleItem?.start?.find(item => item?.sortOrder === 1)?.athleteCode)?.description
    let participant2CP = getSportCodeObject(dataSportCodes, mirs_discipline, '@CompetitorPlace', scheduleItem?.start?.find(item => item?.sortOrder === 2)?.athleteCode)?.description

    participant1CP = participant1CP ? participant1CP : getSportCodeObject(dataSportCodes, mirs_discipline, '@CompetitorPlace', scheduleItem?.start?.find(item => item?.sortOrder === 1)?.teamCode)?.description
    participant2CP = participant2CP ? participant2CP : getSportCodeObject(dataSportCodes, mirs_discipline, '@CompetitorPlace', scheduleItem?.start?.find(item => item?.sortOrder === 2)?.teamCode)?.description

    const participant1 = participant1CP ? participant1CP : (participant1TeamType === 'ORG' && participant1Organisation) ? participant1Organisation : participant1Name;
    const participant2 = participant2CP ? participant2CP : (participant2TeamType === 'ORG' && participant2Organisation) ? participant2Organisation : participant2Name;

    //const participant1 = (participant1TeamType === 'ORG' && participant1Organisation) ? participant1Organisation : participant1Name;
    //const participant2 = (participant2TeamType === 'ORG' && participant2Organisation) ? participant2Organisation : participant2Name;
    const lblStatus = _lblStatus ? _lblStatus : ''
    const gamesShort = fLabel({ data: 'SEOGamesDescriptionShort' })
    const gamesMedium = fLabel({ data: 'SEOGamesDescriptionMedium' })
    const gamesLong = fLabel({ data: 'SEOGamesDescriptionLong' })
    let _discipline = mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC ? discipline : ''
    let presentationGame = "";
    let unitNum = scheduleItem?.unitNum;

    if (unitNum) {
        if (['FEN', 'WFE'].includes(mirs_discipline)) {
            presentationGame = `${fLabel({ data: 'HeaderBout' })}  ${unitNum}`
        } else if ('BKG'.includes(mirs_discipline)) {
            presentationGame = `${fLabel({ data: 'LabelBattle' })}  ${unitNum}`
        } else if ('TKW'.includes(mirs_discipline)) {
            presentationGame = `${fLabel({ data: 'LabelContest' })}  ${unitNum}`
        } else if (['VVO', 'VBS', 'HOC', 'RU7', 'FBL', 'FBB', 'ARC', 'VBV', 'HBL', 'WPO', 'BDM', 'BOC', 'WRU'].includes(mirs_discipline)) {
            presentationGame = `${fLabel({ data: 'HeaderMatch' })}  ${unitNum}`
        } else if (['BOX', 'TEN', 'WTE'].includes(mirs_discipline)) {
            presentationGame = ''
        } else {
            presentationGame = `${fLabel({ data: 'HeaderGame' })}  ${unitNum}`
        }
    }

    let h1Top = "";
    let h1Bottom = "";
    let pageTitle = "";
    let pageDescription = "";
    const isTeam = mirs_rsc_completed?.includes("TEAM")
    let isDisciplinesGroup = ['BDM', 'BKB', 'BKG', 'FBL', 'HBL', 'WPO'].includes(mirs_discipline)
    //Las llamadas a Label como funcion no pueden estar dentro de un if porque se produce el error: Rendered more hooks than during the previous render.
    let lblSEOPageDescriptionResultsEvenUnit = fLabel({ data: "SEOPageDescriptionResultsEvenUnit", values: { Discipline: discipline, EventUnit: eventUnitLongDesc, Game: gamesLong } })
    let lblCompetitorVsCompetitor = fLabel({ data: "SEOCompetitorVsCompetitor", values: { Competitor1: participant1, Competitor2: participant2 } })
    let lblSEOPageDescriptionResultsH2HIndividualStatus = fLabel({ data: "SEOPageDescriptionResultsH2HIndividualStatus", values: { Competitor1: participant1, Competitor2: participant2, EventUnit: eventUnitDescription, Discipline: discipline, UnitNum: presentationGame, Game: gamesLong } })
    let lblSEOPageDescriptionResultsH2HIndividualpageDescription = fLabel({ data: "SEOPageDescriptionResultsH2HIndividual", values: { Discipline: discipline, EventUnit: eventUnitDescription, UnitNum: presentationGame, Game: gamesLong } })
    let lblSEORnkingTeam = mirs_discipline === "EQU" ? fLabel({ data: "LabelTeamEventRanking" }) : fLabel({ data: "LabelTeamMedalsRanking" })
    let lblSEORnkingIndividual = fLabel({ data: "LabelIndividualMedalsRanking" })
    let lblSEOGroupOrPool = isDisciplinesGroup ? fLabel({ data: 'LabelGroup' }) : fLabel({ data: 'LabelPool' })
    let lbl = "";
    let lblResult = fLabel({ data: 'HeaderResults' });
    if (objUrl.url.isCompetitionSchedule) {
        lbl = fLabel({ data: 'CompetitionSchedule' });
        h1Bottom = lbl;
        pageTitle = lbl;
        pageDescription = lbl;
    } else if (objUrl.url.isSchedule) {
        lbl = fLabel({ data: 'ScheduleSort' });
        h1Top = _discipline;
        if (mirs_date && mirs_date !== ALL_URL.ALL_DAYS) {
            const formatDateTime = getDateTimeFormat('DayMonthShort');
            let lblDate = dateFormatNoTz(mirs_date, formatDateTime)
            h1Bottom = eventDescSchedule ? `${eventDescSchedule}, ${lbl} - ${lblDate}` : `${lbl} - ${lblDate}`;
        } else {
            h1Bottom = eventDescSchedule ? `${eventDescSchedule}, ${lbl}` : lbl;
        }
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isAthletes) {
        lbl = fLabel({ data: 'LabelAthletes' });
        h1Top = _discipline;
        h1Bottom = lbl;
        pageTitle = h1Top + " " + h1Bottom;
        pageDescription = h1Top + " " + h1Bottom;
    } else if (objUrl.url.isTeams) {
        lbl = fLabel({ data: 'LabelTeams' });
        h1Bottom = lbl;
        pageTitle = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;

    } else if (objUrl.url.isRecords) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'HeaderRecords' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;

    } else if (objUrl.url.isMedal) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'MedalStandings' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isMedallists) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'LabelMedallists' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isMultiMedallists) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'MultiMedallists' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isBios || objUrl.url.isCoach || objUrl.url.isOfficial) {
        const particName = dataParticipants?.persons?.find(person => person?.code === mirs_code)?.name;
        h1Top = _discipline;
        lbl = particName || '';
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isTeam) {
        const teamItem = dataParticipants?.teams?.find(person => person?.code === mirs_code);
        let teamName = teamItem?.name;
        let disciplineDesc = teamItem?.discipline?.description;
        let event = teamItem?.registeredEvents?.at(0)?.event?.description;
        h1Top = _discipline;
        lbl = `${disciplineDesc} - ${event} - ${teamName}` || '';
        h1Bottom = <TeamHeaderBio data={teamItem} /> || '';
        pageDescription = lbl + " " + h1Top;
        pageTitle = lbl + " " + h1Top;
    } else if (objUrl.url.isEntriesByNoc) {
        const nocItem = nocsList?.nocs?.find(n => n.code === mirs_noc);
        h1Top = _discipline;
        // lbl = `${fLabel({ data: "NOCEntriesHeader" })} - ${nocItem?.description}` || "";
        const lblBase = `${(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? fLabel({ data: "NOCEntriesHeader" }) : fLabel({ data: "NOCEntriesTeamHeader" })}` || "";
        lbl = `${lblBase} - ${nocItem?.description}` || "";
        h1Bottom = <PageHeaderFlag title={lblBase} /> || "";
        pageDescription = lbl + " " + h1Top;
        pageTitle = lbl + " " + h1Top;
    } else if (objUrl.url.isScheduleByNoc) {
        const nocItem = nocsList?.nocs?.find(n => n.code === mirs_noc);
        h1Top = _discipline;
        // lbl = `${fLabel({ data: "NOCScheduleHeader" })} - ${nocItem?.description}` || "";
        // const lblBase = `${(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? fLabel({ data: "NOCScheduleHeader" }).split("-")?.at(0)?.trim():fLabel({ data: "NOCScheduleHeader" }) }` || "";
        const lblBase = `${(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? fLabel({ data: "LabelSchedule" }) : fLabel({ data: "NOCScheduleHeader" })}` || "";
        lbl = `${lblBase} - ${nocItem?.description}` || "";
        h1Bottom = <PageHeaderFlag title={lblBase} /> || "";
        pageDescription = lbl + " " + h1Top;
        pageTitle = lbl + " " + h1Top;
    } else if (objUrl.url.isAthletesByNoc) {
        const nocItem = nocsList?.nocs?.find(n => n.code === mirs_noc);
        h1Top = _discipline;
        lbl = `${(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? fLabel({ data: "LabelAthletes" }) : fLabel({ data: "NOCAthletes" })} - ${nocItem?.description}` || "";
        h1Bottom = <PageHeaderFlag title={(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? <Label data="LabelAthletes" /> : <Label data="NOCAthletes" />} /> || "";
        pageDescription = lbl + " " + h1Top;
        pageTitle = lbl + " " + h1Top;
    } else if (objUrl.url.isProfileByNoc) {
        const nocItem = nocsList?.nocs?.find(n => n.code === mirs_noc);
        h1Top = _discipline;
        lbl = `${nocItem?.description} - ${fLabel({ data: "NOCProfileHeader" })}` || "";
        h1Bottom = <PageHeaderFlag title={""} pageTitle={<Label data={"NOCProfileHeader"} />} /> || "";
        pageDescription = lbl + " " + h1Top;
        pageTitle = lbl + " " + h1Top;
    } else if (objUrl.url.isMedallistsByNoc) {
        const nocItem = nocsList?.nocs?.find(n => n.code === mirs_noc);
        h1Top = _discipline;
        lbl = `${(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? "" : fLabel({ data: "HeaderTeam" })} ${nocItem?.description} - ${fLabel({ data: "NocMedallistsHeader" })}` || "";
        h1Bottom = <PageHeaderFlag title={(nocItem?.code === 'AIN' || nocItem?.code === 'NPA') ? "" : <Label data="HeaderTeam" />} pageTitle={<Label data={"NocMedallistsHeader"} />} /> || "";
        pageDescription = lbl + " " + h1Top;
        pageTitle = lbl + " " + h1Top;
    } else if (objUrl.url.isHorse) {
        const particName = dataParticipants?.horses?.find(person => person?.code === mirs_code)?.name;
        h1Top = _discipline;
        lbl = particName || "";
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isEntries) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'LabelEntries' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isReports) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'Reports' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isRanking) {
        h1Top = `${_discipline} ${eventUnitLongDesc}`;
        h1Bottom = `${isTeam ? lblSEORnkingTeam : lblSEORnkingIndividual}`
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isPool) {
        h1Top = `${_discipline} - ${eventUnitLongDesc}`;
        h1Bottom = lblSEOGroupOrPool
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isBracket) {
        h1Top = `${_discipline} - ${eventUnitLongDesc}`;
        h1Bottom = fLabel({ data: 'LabelBrackets' });
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isCoaches) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'Coaches' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isCompetitionStatus) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'LabelCompetitionStatus' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isHearingSchedule) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'LabelHearingSchedule' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isHorses) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'Horses' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isNocList) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'LabelNocs' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isEntriesDiscipline) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'LabelEntriesDiscipline' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isNocEntriesByEvent) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'NOCEntriesByEvent' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isTechnicalOfficials) {
        h1Top = _discipline;
        lbl = fLabel({ data: 'TechnicalOfficials' });
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isTodayMatches) {
        h1Top = _discipline;
        if ('|WRE|JUD|'.includes(mirs_discipline)) {
            lbl = fLabel({ data: 'LabelTodayMatches' });
        } else {
            lbl = fLabel({ data: 'HeaderLiveMatches' });
        }
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isTodayAthMatches) {
        h1Top = _discipline;
        if (mirs_discipline === 'JUD') {
            lbl = fLabel({ data: 'LabelTodayContests' });
        } else {
            lbl = fLabel({ data: 'HeaderLiveMatches' });
        }
        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isTodayTeamMatches) {
        h1Top = _discipline;
        /*
        if (mirs_discipline === 'JUD') {
            lbl = fLabel({ data: 'LabelTodayContests' });
        } else {
            lbl = fLabel({ data: 'LabelTodayMatches' });
        }
        */
        lbl = fLabel({ data: 'LabelTodayMatches' });

        h1Bottom = lbl;
        pageTitle = h1Bottom + " " + h1Top;
        pageDescription = h1Bottom + " " + h1Top;
    } else if (objUrl.url.isResult) {

        if (isH2H) { //Result H2H
            if (lblStatus && (participant1 && participant2)) {
                h1Top = `${discipline} - ${eventUnitLongDesc} ${presentationGame}`;
                h1Bottom = `${lblCompetitorVsCompetitor} ${lblResult}`
                pageDescription = lblSEOPageDescriptionResultsH2HIndividualStatus;
                pageTitle = `${lblCompetitorVsCompetitor} - ${eventUnitLongDesc}  ${presentationGame} - ${discipline}  | ${gamesMedium}`;
            } else {
                h1Top = discipline;
                h1Bottom = eventUnitDescription && (`${eventUnitDescription} ${presentationGame} ${lblResult}`)  //eventUnitLongDesc;
                pageDescription = lblSEOPageDescriptionResultsH2HIndividualpageDescription;
                pageTitle = `${eventUnitDescription} - ${discipline} ${presentationGame} | ${gamesMedium}`;
            }
        } else { //Result  no H2H El PO nos obliga a quitar el Status, no hay que hacer caso al PDF
            h1Top = discipline;
            h1Bottom = `${eventUnitLongDesc} ${lblResult}`;
            pageTitle = `${eventUnitDescription} - ${discipline} | ${gamesMedium}`;
            pageDescription = lblSEOPageDescriptionResultsEvenUnit;
        }

    }
    let h1Object = { lineTop: h1Top, lineBottom: h1Bottom };
    return { h1Object: h1Object, pageTitle: pageTitle, pageDescription: pageDescription }
}
